import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePartnerRatings } from '@/hooks/usePartnerRatings';
import { Star, TrendingUp, TrendingDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { RatingDisplay } from './RatingDisplay';

interface RatingsHistoryListProps {
  companyId: string;
}

export function RatingsHistoryList({ companyId }: RatingsHistoryListProps) {
  const { ratings, isLoading, averageRatings } = usePartnerRatings(companyId);

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading ratings...</div>;
  }

  if (ratings.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No ratings yet</p>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (value: number | undefined) => {
    if (!value) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {averageRatings && (
        <Card>
          <CardHeader>
            <CardTitle>Average Ratings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Overall</p>
                <div className="flex items-center justify-center gap-1">
                  <Star className="h-5 w-5 fill-primary text-primary" />
                  <span className="text-2xl font-bold">{averageRatings.overall.toFixed(2)}</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Safety</p>
                <RatingDisplay rating={averageRatings.safety} size="sm" />
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Quality</p>
                <RatingDisplay rating={averageRatings.quality} size="sm" />
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Reliability</p>
                <RatingDisplay rating={averageRatings.reliability} size="sm" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {ratings.map((rating) => (
          <Card key={rating.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="h-6 w-6 fill-primary text-primary" />
                    <span className="text-2xl font-bold">{rating.overall_rating?.toFixed(2)}</span>
                    <span className="text-muted-foreground">Overall Rating</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rated on {format(parseISO(rating.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
                {rating.contract_value && (
                  <Badge variant="secondary" className="text-lg">
                    {formatCurrency(rating.contract_value)}
                  </Badge>
                )}
              </div>

              {(rating.rating_period_start || rating.rating_period_end) && (
                <div className="mb-4 text-sm text-muted-foreground">
                  Rating Period: {rating.rating_period_start && format(parseISO(rating.rating_period_start), 'MMM d, yyyy')}
                  {rating.rating_period_end && ` - ${format(parseISO(rating.rating_period_end), 'MMM d, yyyy')}`}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Safety</p>
                  <RatingDisplay rating={rating.safety_rating || 0} size="sm" />
                  {rating.safety_notes && (
                    <p className="text-xs text-muted-foreground mt-2">{rating.safety_notes}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">Quality</p>
                  <RatingDisplay rating={rating.quality_rating || 0} size="sm" />
                  {rating.quality_notes && (
                    <p className="text-xs text-muted-foreground mt-2">{rating.quality_notes}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium">Reliability</p>
                  <RatingDisplay rating={rating.reliability_rating || 0} size="sm" />
                  {rating.reliability_notes && (
                    <p className="text-xs text-muted-foreground mt-2">{rating.reliability_notes}</p>
                  )}
                </div>
              </div>

              {rating.overall_notes && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">{rating.overall_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
